/**
 * Runtime-only mesh state that cannot be stored in data model.
 * Singleton module - accessible from anywhere without prop drilling.
 *
 * This replaces userData properties that are runtime flags (not persisted data).
 * For persisted element data, use elementIndex instead.
 *
 * Two types of state:
 * 1. Boolean flags (Sets) - for simple markers like "is this a highlight overlay?"
 * 2. Associated data (Maps) - for runtime elements with metadata (textures, handles)
 */

import {
  advanceOpenClose,
  beginClosing,
  beginOpening,
  landsAt,
  openCloseStateOf,
  toggleWouldOpen,
  OPEN_CLOSE_STATE,
} from './openCloseAnimation';

const state = {
  // Highlight overlays (created/destroyed during interaction)
  highlightOverlays: new Set(),

  // Structural group markers (for raycast traversal)
  proceduralGroups: new Set(),
  openingGroups: new Set(),

  // Wall face markers (for dollhouse mode)
  interiorFaces: new Set(),
  exteriorFaces: new Set(),

  // Texture geometry metadata (uuid -> {tileGeometryFor, wallNormal, regionId})
  textureMetadata: new Map(),

  // Region handle metadata (uuid -> {regionIndex, handleType, isRegionHandle, isRegionOutline})
  handleMetadata: new Map(),

  // Fixture metadata (for lighting) (uuid -> {fixtureId, originalIntensity, isModified})
  fixtureMetadata: new Map(),

  // Selection state (uuids of currently selected objects)
  selectedObjects: new Set(),

  // Debug object markers (for cleanup in dev/debug tools)
  debugObjects: new Set(),

  // Debug helper metadata (uuid -> {parentWallIds, isClickedFace, isClickedNormal})
  debugHelperMetadata: new Map(),

  // Structure object markers (for PreTransformedViewer cleanup)
  structureObjects: new Set(),

  // Object runtime metadata (uuid -> {id, dimensions, currentDimensions, originalDimensions, originalObjectData})
  objectMetadata: new Map(),

  // Highlight overlay metadata (uuid -> {highlightType, objectId})
  highlightMetadata: new Map(),

  // Highlight groups by object ID, one per channel (objectId -> Map(type -> overlayGroup))
  highlightGroups: new Map(),

  // Cutout metadata (uuid -> {hasCutouts, sinkModelKey})
  cutoutMetadata: new Map(),

  // Wall metadata (uuid -> {type, isPolygonWall, wallId, originalWallId, originalWallData, roomId, hasOpenings, openings, worldPosition, worldRotation, dimensions, edgeIndex})
  wallMetadata: new Map(),

  // Mesh-to-Object grouping (for highlighting/selection)
  // Maps Three.js mesh UUIDs to database object IDs
  meshToObject: new Map(), // meshUuid -> objectId
  objectMeshes: new Map(), // objectId -> Set<meshUuid>
  objectRoots: new Map(), // objectId -> Three.js object reference

  // Pending callbacks waiting for mesh registration
  // objectId -> Array<{ resolve, timer }>
  pendingMeshCallbacks: new Map(),

  // Electrical switch states (switchId -> boolean, true = on)
  // Switches start as "on" by default
  switchStates: new Map(),

  // First-person door open state (doorId -> { state, openedAt, triggerRoomId }).
  // state: 'closed' (default, not in map) | 'opening' | 'open'.
  // Doors stay open once opened — no auto-close.
  // triggerRoomId records the FP room that initiated the open, so the
  // ghosting from per-side door copies can be hidden by rendering only
  // the copy whose owner room matches.
  doorOpenState: new Map(),

  // Cabinet door / drawer fronts, keyed `<objectId>:<frontName>` (see
  // `cabinetFrontStateKey`). Same four-state machine as doors, via the shared
  // `openCloseAnimation` core; runtime only, so a reload closes everything.
  cabinetFrontOpenState: new Map(),

  // While true, proximity auto-open leaves every door alone. Set only by the
  // automatic-capture bridge (hooks/useCaptureBridge.js): seating a camera in a
  // small room puts it inside some door's 5 ft trigger, and the door swinging
  // into the room lands in the middle of the photograph.
  proximityDoorsSuspended: false,

  // FP camera's current room id (null when outside any known room).
  // ArchitecturalScene tracks this via point-in-polygon and writes it
  // here so anyone (Door rendering, FirstPersonMovement bump trigger)
  // can read it without prop drilling.
  firstPersonRoomId: null,

  // Registry of mounted Door copies keyed by canonical door id
  // (`structureOpeningId || id`) → set of owner room ids that have a
  // mounted copy. Used to fail-safe the "hide non-matching copies"
  // rule: if no mounted copy matches the trigger room, fall back to
  // showing all copies so we never end up with an invisible door.
  doorCopiesByKey: new Map(),

  // Light original emissive values for restore (lightMeshUuid -> {color, intensity})
  lightOriginalEmissive: new Map(),

  // Material mode flag: true = realistic (MeshStandardMaterial), false = white mode (MeshBasicMaterial).
  // Toggled by the visibility-controls White Mode toggle.
  hasEditedModels: true,

  // Original MeshStandardMaterials stored before downgrade (meshUuid -> original material)
  originalMaterials: new Map(),

  // Monotonic counter bumped whenever the mesh graph changes in a way a
  // scene-derived cache needs to react to: a mesh registers (added to the
  // scene by a geometry renderer — PolygonWalls, Door, Floor, Window, …) or
  // mesh state is cleared (removed, via clearMeshState/clearAllState).
  // There's no cheaper signal to key such a cache on: `scene` itself stays
  // the same object across renders, and `scene.children.length` can't see a
  // nested addition/removal (walls, doors, etc. mount several levels deep).
  // See `checkWallCollision`'s collision-mesh cache in
  // `components/3d/controls/utils/navigationAndEditHandlers.js`.
  meshRegistryVersion: 0,
};

// Highlight overlays
export const markHighlightOverlay = (uuid, metadata = {}) => {
  state.highlightOverlays.add(uuid);
  if (metadata.highlightType || metadata.objectId) {
    state.highlightMetadata.set(uuid, metadata);
  }
};
export const unmarkHighlightOverlay = (uuid) => {
  state.highlightOverlays.delete(uuid);
  state.highlightMetadata.delete(uuid);
};
export const isHighlightOverlay = (uuid) => state.highlightOverlays.has(uuid);
export const getHighlightMetadata = (uuid) => state.highlightMetadata.get(uuid);

// Highlight groups: objectId -> Map(type -> overlayGroup). One slot per
// (object, CHANNEL), so a hover — or the inventory's "Show me" flash — can be
// painted and removed on an object without touching its selection overlay.
// (A single slot per object used to let the second channel overwrite the
// first's entry, orphaning its overlay in the scene.)
export const setHighlightGroup = (objectId, overlayGroup, type) => {
  let byType = state.highlightGroups.get(objectId);
  if (!byType) {
    byType = new Map();
    state.highlightGroups.set(objectId, byType);
  }
  byType.set(type, overlayGroup);
};
/** The `{ overlayGroup, type }` of one channel on an object, or undefined. */
export const getHighlightGroup = (objectId, type = 'selection') => {
  const overlayGroup = state.highlightGroups.get(objectId)?.get(type);
  return overlayGroup ? { overlayGroup, type } : undefined;
};
/** The channels an object currently has a highlight on. */
export const getHighlightTypes = (objectId) => [
  ...(state.highlightGroups.get(objectId)?.keys() ?? []),
];
/** Forget one channel's group on an object (every channel when no type). */
export const clearHighlightGroup = (objectId, type) => {
  const byType = state.highlightGroups.get(objectId);
  if (!byType) return;
  if (type === undefined) byType.clear();
  else byType.delete(type);
  if (byType.size === 0) state.highlightGroups.delete(objectId);
};
export const getHighlightGroupsByType = (type) => {
  const result = [];
  for (const [objectId, byType] of state.highlightGroups) {
    const overlayGroup = byType.get(type);
    if (overlayGroup) result.push({ objectId, overlayGroup, type });
  }
  return result;
};
export const clearHighlightGroupsByType = (type) => {
  for (const objectId of [...state.highlightGroups.keys()]) {
    clearHighlightGroup(objectId, type);
  }
};

// Procedural groups
export const markProceduralGroup = (uuid) => state.proceduralGroups.add(uuid);
export const unmarkProceduralGroup = (uuid) =>
  state.proceduralGroups.delete(uuid);
export const isProceduralGroup = (uuid) => state.proceduralGroups.has(uuid);

// Opening groups
export const markOpeningGroup = (uuid) => state.openingGroups.add(uuid);
export const unmarkOpeningGroup = (uuid) => state.openingGroups.delete(uuid);
export const isOpeningGroup = (uuid) => state.openingGroups.has(uuid);

// Wall faces
export const markInteriorFace = (uuid) => state.interiorFaces.add(uuid);
export const unmarkInteriorFace = (uuid) => state.interiorFaces.delete(uuid);
export const isInteriorFace = (uuid) => state.interiorFaces.has(uuid);

export const markExteriorFace = (uuid) => state.exteriorFaces.add(uuid);
export const unmarkExteriorFace = (uuid) => state.exteriorFaces.delete(uuid);
export const isExteriorFace = (uuid) => state.exteriorFaces.has(uuid);

// Texture metadata (for tile geometry cleanup)
export const setTextureMetadata = (uuid, metadata) =>
  state.textureMetadata.set(uuid, metadata);
export const getTextureMetadata = (uuid) => state.textureMetadata.get(uuid);
export const clearTextureMetadata = (uuid) =>
  state.textureMetadata.delete(uuid);

/**
 * Disposes a tile mesh and clears its metadata from runtimeMeshState.
 * Handles cleanup of the tile and all its children, including:
 * - Clearing texture metadata
 * - Removing from parent
 * - Disposing geometry and materials
 * @param {Object} tile - Three.js mesh/group to dispose
 */
export const disposeTileWithMetadata = (tile) => {
  // Clear metadata for tile and all children
  tile.traverse((child) => {
    if (child.isMesh || child.isGroup) {
      clearTextureMetadata(child.uuid);
    }
  });
  clearTextureMetadata(tile.uuid);

  // Remove from parent
  if (tile.parent) tile.parent.remove(tile);

  // Dispose geometry
  if (tile.geometry) tile.geometry.dispose();

  // Dispose materials AND their textures.
  // Tile materials use two texture slots: `map` (a per-tile clone handed out
  // by textureCache.loadTexture — distinct GPU upload, safe to dispose) and
  // `alphaMap` (a per-tile CanvasTexture from createRegionAlphaMap). Neither is
  // the shared cache master, so disposing them frees only this tile's uploads.
  // Tiles never set normalMap/roughnessMap/aoMap/metalnessMap, so those are
  // intentionally excluded.
  if (tile.material) {
    const materials = Array.isArray(tile.material)
      ? tile.material
      : [tile.material];
    materials.forEach((mat) => {
      mat.map?.dispose();
      mat.alphaMap?.dispose();
      mat.dispose();
    });
  }
};

export const getTexturesForSegment = (segmentUuid) => {
  const result = [];
  for (const [uuid, meta] of state.textureMetadata) {
    if (meta.tileGeometryFor === segmentUuid) {
      result.push({ uuid, ...meta });
    }
  }
  return result;
};

// Handle metadata (for region selection handles)
export const setHandleMetadata = (uuid, metadata) =>
  state.handleMetadata.set(uuid, metadata);
export const getHandleMetadata = (uuid) => state.handleMetadata.get(uuid);
export const clearHandleMetadata = (uuid) => state.handleMetadata.delete(uuid);

// Fixture metadata (for lighting)
export const setFixtureMetadata = (uuid, metadata) =>
  state.fixtureMetadata.set(uuid, metadata);
export const getFixtureMetadata = (uuid) => state.fixtureMetadata.get(uuid);
export const clearFixtureMetadata = (uuid) =>
  state.fixtureMetadata.delete(uuid);

// Selection state
export const markSelected = (uuid) => state.selectedObjects.add(uuid);
export const unmarkSelected = (uuid) => state.selectedObjects.delete(uuid);
export const isSelected = (uuid) => state.selectedObjects.has(uuid);
export const clearAllSelected = () => state.selectedObjects.clear();
export const getSelectedObjects = () => [...state.selectedObjects];

// Debug object markers
export const markDebugObject = (uuid) => state.debugObjects.add(uuid);
export const unmarkDebugObject = (uuid) => state.debugObjects.delete(uuid);
export const isDebugObject = (uuid) => state.debugObjects.has(uuid);

// Debug helper metadata (for textureGeometry debug visualizations)
export const setDebugHelperMetadata = (uuid, metadata) => {
  state.debugObjects.add(uuid); // Also mark as debug object
  state.debugHelperMetadata.set(uuid, metadata);
};
export const getDebugHelperMetadata = (uuid) =>
  state.debugHelperMetadata.get(uuid);
export const clearDebugHelperMetadata = (uuid) => {
  state.debugObjects.delete(uuid);
  state.debugHelperMetadata.delete(uuid);
};

// Structure object markers
export const markStructureObject = (uuid) => state.structureObjects.add(uuid);
export const unmarkStructureObject = (uuid) =>
  state.structureObjects.delete(uuid);
export const isStructureObject = (uuid) => state.structureObjects.has(uuid);

// Object runtime metadata (for dimension tracking)
export const setObjectMetadata = (uuid, metadata) => {
  const existing = state.objectMetadata.get(uuid) || {};
  state.objectMetadata.set(uuid, { ...existing, ...metadata });
};
export const getObjectMetadata = (uuid) => state.objectMetadata.get(uuid);
export const clearObjectMetadata = (uuid) => state.objectMetadata.delete(uuid);
export const getObjectId = (uuid) => state.objectMetadata.get(uuid)?.id;

// Cutout metadata (for cabinet sink cutouts)
export const setCutoutMetadata = (uuid, metadata) => {
  const existing = state.cutoutMetadata.get(uuid) || {};
  state.cutoutMetadata.set(uuid, { ...existing, ...metadata });
};
export const getCutoutMetadata = (uuid) => state.cutoutMetadata.get(uuid);
export const clearCutoutMetadata = (uuid) => state.cutoutMetadata.delete(uuid);

// Wall metadata (for polygon walls runtime state)
export const setWallMetadata = (uuid, metadata) => {
  const existing = state.wallMetadata.get(uuid) || {};
  state.wallMetadata.set(uuid, { ...existing, ...metadata });
};
export const getWallMetadata = (uuid) => state.wallMetadata.get(uuid);
export const clearWallMetadata = (uuid) => state.wallMetadata.delete(uuid);

// Mesh-to-Object grouping (for highlighting/selection)
// Links Three.js mesh UUIDs to database object IDs
export const registerMesh = (meshUuid, objectId) => {
  // Remove from old object if re-registering
  const oldObjectId = state.meshToObject.get(meshUuid);
  if (oldObjectId && oldObjectId !== objectId) {
    state.objectMeshes.get(oldObjectId)?.delete(meshUuid);
  }

  // Add to new object
  state.meshToObject.set(meshUuid, objectId);
  if (!state.objectMeshes.has(objectId)) {
    state.objectMeshes.set(objectId, new Set());
  }
  state.objectMeshes.get(objectId).add(meshUuid);

  // Notify any pending waiters for this objectId
  if (state.pendingMeshCallbacks.has(objectId)) {
    const callbacks = state.pendingMeshCallbacks.get(objectId);
    callbacks.forEach(({ resolve, timer }) => {
      clearTimeout(timer);
      resolve(objectId);
    });
    state.pendingMeshCallbacks.delete(objectId);
  }

  bumpMeshRegistryVersion();
};

export const unregisterMesh = (meshUuid) => {
  const objectId = state.meshToObject.get(meshUuid);
  if (objectId) {
    state.objectMeshes.get(objectId)?.delete(meshUuid);
    // Clean up empty sets
    if (state.objectMeshes.get(objectId)?.size === 0) {
      state.objectMeshes.delete(objectId);
    }
  }
  state.meshToObject.delete(meshUuid);
};

export const getObjectForMesh = (meshUuid) => state.meshToObject.get(meshUuid);

// Mesh registry version (see field doc above). Bumped by registerMesh and by
// the removal-side cleanup (clearMeshState / clearAllState) below.
export const getMeshRegistryVersion = () => state.meshRegistryVersion;
const bumpMeshRegistryVersion = () => {
  state.meshRegistryVersion += 1;
};

export const getMeshesForObject = (objectId) =>
  state.objectMeshes.get(objectId) || new Set();

// Object root registration (for direct lookup without hierarchy traversal)
export const registerObjectRoot = (objectId, rootObject) => {
  state.objectRoots.set(objectId, rootObject);
};

export const getObjectRoot = (objectId) => state.objectRoots.get(objectId);

export const clearObjectRoot = (objectId) => state.objectRoots.delete(objectId);

export const clearObjectMeshes = (objectId) => {
  const meshes = state.objectMeshes.get(objectId);
  if (meshes) {
    for (const meshUuid of meshes) {
      state.meshToObject.delete(meshUuid);
    }
    state.objectMeshes.delete(objectId);
  }
  state.objectRoots.delete(objectId);
};

// Check if a mesh/object is already registered (synchronous)
export const isMeshReady = (objectId) => {
  return state.objectRoots.has(objectId) || state.objectMeshes.has(objectId);
};

// Wait for a mesh to be registered (Promise-based)
// Resolves immediately if already registered, otherwise waits for registerMesh()
// Rejects after timeout if mesh never registers
export const waitForMesh = (objectId, timeoutMs = 5000) => {
  return new Promise((resolve, reject) => {
    // If already registered, resolve immediately
    if (isMeshReady(objectId)) {
      resolve(objectId);
      return;
    }

    // Set up callback to be called when mesh registers
    if (!state.pendingMeshCallbacks.has(objectId)) {
      state.pendingMeshCallbacks.set(objectId, []);
    }

    // Set up timeout
    const timer = setTimeout(() => {
      // Clean up this callback on timeout
      const callbacks = state.pendingMeshCallbacks.get(objectId);
      if (callbacks) {
        const idx = callbacks.findIndex((c) => c.timer === timer);
        if (idx !== -1) callbacks.splice(idx, 1);
        if (callbacks.length === 0) state.pendingMeshCallbacks.delete(objectId);
      }
      reject(
        new Error(`Mesh ${objectId} not registered within ${timeoutMs}ms`)
      );
    }, timeoutMs);

    state.pendingMeshCallbacks.get(objectId).push({ resolve, reject, timer });
  });
};

// Wait for multiple meshes to be registered
export const waitForMeshes = (objectIds, timeoutMs = 5000) => {
  return Promise.all(objectIds.map((id) => waitForMesh(id, timeoutMs)));
};

// Cancel all pending mesh callbacks (for cleanup)
const clearPendingMeshCallbacks = () => {
  for (const [, callbacks] of state.pendingMeshCallbacks) {
    callbacks.forEach(({ timer }) => clearTimeout(timer));
  }
  state.pendingMeshCallbacks.clear();
};

// Electrical switch states (for toggling lights)
// Switches default to "on" (true) when first accessed
export const getSwitchState = (switchId) => {
  if (!state.switchStates.has(switchId)) {
    state.switchStates.set(switchId, true); // Default to on
  }
  return state.switchStates.get(switchId);
};
export const setSwitchState = (switchId, isOn) =>
  state.switchStates.set(switchId, isOn);
export const toggleSwitchState = (switchId) => {
  const currentState = getSwitchState(switchId);
  state.switchStates.set(switchId, !currentState);
  return !currentState; // Return new state
};
export const clearSwitchStates = () => state.switchStates.clear();

// First-person door animation state. Two-stage interaction model:
// the camera can't walk through a closed door — it must bump (one
// forward press), then press forward again to trigger the open.
// Proximity auto-open (useProximityDoors) opens them earlier still.
// Clicking a door's handle toggles it open/shut in either direction.
//
// State values:
//   'closed'  — at rest, shut (a door that was never touched has no entry)
//   'opening' — animating toward open (Door.jsx lerps rotation.y / slide)
//   'open'    — at rest, fully open
//   'closing' — animating back toward shut
//
// An entry carries the animation's start time and the progress it started
// FROM, so a toggle that interrupts a half-finished swing reverses out of
// the door's current angle instead of snapping to an endpoint first.
export const DOOR_OPEN_DURATION_MS = 1000;
export const getDoorOpenState = (doorId) =>
  state.doorOpenState.get(doorId)?.state || 'closed';
// A door is walk-through-able the instant it starts opening, and stays so
// while it swings shut — going solid mid-close would trap a walker standing
// in the doorway.
export const isDoorPassable = (doorId) => {
  const s = state.doorOpenState.get(doorId)?.state;
  return s === 'opening' || s === 'open' || s === 'closing';
};
// True once the user has hand-closed this door: proximity auto-open leaves
// it alone until something opens it again (the two-stage bump, or another
// handle click). See useProximityDoors.
export const isDoorAutoOpenSuppressed = (doorId) =>
  state.doorOpenState.get(doorId)?.manualClose === true;

// Progress toward open at time `now`: 0 shut, 1 fully open. Reads as a
// getter but deliberately promotes a landed animation to its terminal
// state — one call per door per frame from Door.jsx's useFrame drives the
// whole machine, so there's nowhere else the transition could happen.
// The timing math is the shared core in `openCloseAnimation.js`.
export const advanceDoorAnimation = (doorId, now = performance.now()) => {
  const entry = state.doorOpenState.get(doorId);
  const { t, entry: next } = advanceOpenClose(
    entry,
    now,
    DOOR_OPEN_DURATION_MS
  );
  if (next !== entry) state.doorOpenState.set(doorId, next);
  return t;
};

export const startOpeningDoor = (doorId, options = {}) => {
  const cur = state.doorOpenState.get(doorId);
  const { now = performance.now(), triggerRoomId } = options;
  const next = beginOpening(cur, now, DOOR_OPEN_DURATION_MS);
  if (!next) return;
  state.doorOpenState.set(doorId, {
    ...next,
    triggerRoomId:
      triggerRoomId ?? cur?.triggerRoomId ?? state.firstPersonRoomId,
    // Opening it again re-arms proximity auto-open.
    manualClose: false,
  });
};

export const startClosingDoor = (doorId, options = {}) => {
  const cur = state.doorOpenState.get(doorId);
  const { now = performance.now(), triggerRoomId, manual = false } = options;
  const next = beginClosing(cur, now, DOOR_OPEN_DURATION_MS);
  if (!next) return;
  state.doorOpenState.set(doorId, {
    ...next,
    // Re-stamp the trigger room to the copy the user acted on, so the panel
    // swings shut in the room they're looking at rather than the one it was
    // opened from (see the ghost-fix visibility rule in Door.jsx).
    triggerRoomId: triggerRoomId ?? cur?.triggerRoomId ?? null,
    manualClose: manual,
  });
};

/**
 * The hand-gesture entry point: flip a door toward whichever end it isn't
 * heading for. Closing this way counts as manual (suppresses auto-open)
 * unless the caller says otherwise.
 */
export const toggleDoor = (doorId, options = {}) => {
  if (toggleWouldOpen(state.doorOpenState.get(doorId))) {
    startOpeningDoor(doorId, options);
  } else {
    startClosingDoor(doorId, { manual: true, ...options });
  }
};

export const clearDoorOpenStates = () => state.doorOpenState.clear();

// Cabinet fronts — a door swinging on its hinge or a drawer sliding out.
// Driven by ProceduralObject's useFrame the way Door.jsx drives doors: one
// `advanceCabinetFrontAnimation` per front per frame is the machine's clock.
export const CABINET_FRONT_OPEN_DURATION_MS = 450;
export const cabinetFrontStateKey = (objectId, frontName) =>
  `${objectId}:${frontName}`;
export const getCabinetFrontOpenState = (key) =>
  openCloseStateOf(state.cabinetFrontOpenState.get(key));
export const advanceCabinetFrontAnimation = (key, now = performance.now()) => {
  const entry = state.cabinetFrontOpenState.get(key);
  if (!entry) return 0;
  const { t, entry: next } = advanceOpenClose(
    entry,
    now,
    CABINET_FRONT_OPEN_DURATION_MS
  );
  if (next !== entry) state.cabinetFrontOpenState.set(key, next);
  return t;
};
/** Flip one front toward whichever end it isn't heading for. */
export const toggleCabinetFront = (key, now = performance.now()) => {
  const cur = state.cabinetFrontOpenState.get(key);
  const next = toggleWouldOpen(cur)
    ? beginOpening(cur, now, CABINET_FRONT_OPEN_DURATION_MS)
    : beginClosing(cur, now, CABINET_FRONT_OPEN_DURATION_MS);
  if (next) state.cabinetFrontOpenState.set(key, next);
};
/**
 * Start opening one front; a front already open or opening is left alone
 * (never toggled shut) — the inventory list's "Show me".
 */
export const openCabinetFront = (key, now = performance.now()) => {
  const next = beginOpening(
    state.cabinetFrontOpenState.get(key),
    now,
    CABINET_FRONT_OPEN_DURATION_MS
  );
  if (next) state.cabinetFrontOpenState.set(key, next);
};
/**
 * Move one cabinet's fronts in STAGES: every front of stage k+1 starts the
 * moment the last of stage k lands — a roll-out cabinet's doors swing open
 * before its trays slide out, one tray after another, and on the way shut the
 * trays go in before the doors close. A front already at the stage's end (or
 * on its way there) is left alone and holds up nothing past its own landing.
 * Mid-animation reversal comes from the core: each front starts from the pose
 * it has now.
 *
 * @param {string} objectId
 * @param {string[][]} stages  front names, in the order they move
 * @returns {number} ms from `now` until the last front lands (0 if none moves)
 */
const runCabinetFrontStages = (objectId, stages, now, begin) => {
  let startAt = now;
  let last = now;
  for (const names of stages) {
    let stageLands = startAt;
    for (const name of names) {
      const key = cabinetFrontStateKey(objectId, name);
      const cur = state.cabinetFrontOpenState.get(key);
      const next = begin(cur, now, CABINET_FRONT_OPEN_DURATION_MS, startAt);
      if (next) state.cabinetFrontOpenState.set(key, next);
      const entry = next || cur;
      stageLands = Math.max(
        stageLands,
        landsAt(entry, CABINET_FRONT_OPEN_DURATION_MS)
      );
    }
    startAt = stageLands;
    last = Math.max(last, stageLands);
  }
  return last - now;
};
export const openCabinetFrontsInStages = (
  objectId,
  stages,
  now = performance.now()
) => runCabinetFrontStages(objectId, stages, now, beginOpening);
export const closeCabinetFrontsInStages = (
  objectId,
  stages,
  now = performance.now()
) => runCabinetFrontStages(objectId, stages, now, beginClosing);
/**
 * Drop the state of every front of `objectId` that is no longer drawn
 * (`isLive(frontName)` false). A front removed mid-animation — roll-outs
 * turned off while the trays are out — is never advanced again, so without
 * this it stays `opening` forever and `isAnyCabinetFrontMoving` keeps the
 * cabinet's hover highlight off. Called every frame per procedural object,
 * so it returns at once when no front state is held anywhere, and otherwise
 * makes one pass over the held fronts (deleting the current key while
 * iterating a Map is safe).
 */
export const forgetCabinetFronts = (objectId, isLive) => {
  if (state.cabinetFrontOpenState.size === 0) return;
  const prefix = `${objectId}:`;
  for (const key of state.cabinetFrontOpenState.keys()) {
    if (key.startsWith(prefix) && !isLive(key.slice(prefix.length))) {
      state.cabinetFrontOpenState.delete(key);
    }
  }
};
export const clearCabinetFrontOpenStates = () =>
  state.cabinetFrontOpenState.clear();
/** Is any front of object `objectId` swinging or sliding right now? */
export const isAnyCabinetFrontMoving = (objectId) => {
  const prefix = `${objectId}:`;
  for (const [key, entry] of state.cabinetFrontOpenState) {
    if (!key.startsWith(prefix)) continue;
    const s = openCloseStateOf(entry);
    if (s === OPEN_CLOSE_STATE.opening || s === OPEN_CLOSE_STATE.closing) {
      return true;
    }
  }
  return false;
};

export const setProximityDoorsSuspended = (suspended) => {
  state.proximityDoorsSuspended = Boolean(suspended);
};
export const areProximityDoorsSuspended = () => state.proximityDoorsSuspended;
export const getDoorTriggerRoomId = (doorId) =>
  state.doorOpenState.get(doorId)?.triggerRoomId ?? null;

// FP camera room (singleton accessor used by Door rendering and the
// movement loop). React consumers (e.g. the editor's editMode derivation +
// the leave-FP handoff) subscribe to be notified when the walker crosses
// into a different room.
const firstPersonRoomIdSubscribers = new Set();
export const getFirstPersonRoomId = () => state.firstPersonRoomId;
export const setFirstPersonRoomId = (id) => {
  const next = id ?? null;
  if (next === state.firstPersonRoomId) return;
  state.firstPersonRoomId = next;
  firstPersonRoomIdSubscribers.forEach((cb) => cb(next));
};
/**
 * Subscribe to first-person room changes. Returns an unsubscribe fn.
 * @param {(roomId: string|null) => void} cb
 */
export const subscribeFirstPersonRoomId = (cb) => {
  firstPersonRoomIdSubscribers.add(cb);
  return () => firstPersonRoomIdSubscribers.delete(cb);
};

// Door copy registry — a Door component registers its (canonicalKey,
// ownerRoomId) on mount, unregisters on unmount. Used as a fail-safe
// for the "hide non-matching copies" rule so a door is never
// completely invisible if the trigger room has no rendered copy.
export const registerDoorCopy = (doorKey, ownerRoomId) => {
  if (!doorKey) return;
  let set = state.doorCopiesByKey.get(doorKey);
  if (!set) {
    set = new Set();
    state.doorCopiesByKey.set(doorKey, set);
  }
  set.add(ownerRoomId ?? null);
};
export const unregisterDoorCopy = (doorKey, ownerRoomId) => {
  if (!doorKey) return;
  const set = state.doorCopiesByKey.get(doorKey);
  if (!set) return;
  set.delete(ownerRoomId ?? null);
  if (set.size === 0) state.doorCopiesByKey.delete(doorKey);
};
export const hasDoorCopyForRoom = (doorKey, ownerRoomId) => {
  if (!doorKey) return false;
  return state.doorCopiesByKey.get(doorKey)?.has(ownerRoomId ?? null) ?? false;
};

// Light original emissive values (for restore when turning back on)
export const setLightOriginalEmissive = (meshUuid, color, intensity) =>
  state.lightOriginalEmissive.set(meshUuid, { color, intensity });
export const getLightOriginalEmissive = (meshUuid) =>
  state.lightOriginalEmissive.get(meshUuid);
export const clearLightOriginalEmissive = (meshUuid) =>
  state.lightOriginalEmissive.delete(meshUuid);

// Material mode flag (hasEditedModels) — toggled by visibility-controls White Mode
export const getHasEditedModels = () => state.hasEditedModels;
export const initHasEditedModels = (value) => {
  state.hasEditedModels = !!value;
};

// Original material storage (for downgrade/upgrade cycle)
export const storeOriginalMaterial = (uuid, material) =>
  state.originalMaterials.set(uuid, material);
export const getOriginalMaterial = (uuid) => state.originalMaterials.get(uuid);

// Cleanup when mesh removed
export const clearMeshState = (uuid) => {
  unregisterMesh(uuid); // Remove from mesh-object grouping
  state.highlightOverlays.delete(uuid);
  state.proceduralGroups.delete(uuid);
  state.openingGroups.delete(uuid);
  state.interiorFaces.delete(uuid);
  state.exteriorFaces.delete(uuid);
  state.textureMetadata.delete(uuid);
  state.handleMetadata.delete(uuid);
  state.fixtureMetadata.delete(uuid);
  state.selectedObjects.delete(uuid);
  state.debugObjects.delete(uuid);
  state.debugHelperMetadata.delete(uuid);
  state.structureObjects.delete(uuid);
  state.objectMetadata.delete(uuid);
  state.highlightMetadata.delete(uuid);
  state.cutoutMetadata.delete(uuid);
  state.wallMetadata.delete(uuid);
  state.lightOriginalEmissive.delete(uuid);
  state.originalMaterials.delete(uuid);
  bumpMeshRegistryVersion();
};

// Clear all (for scene reset)
export const clearAllState = () => {
  state.highlightOverlays.clear();
  state.highlightGroups.clear();
  state.proceduralGroups.clear();
  state.openingGroups.clear();
  state.interiorFaces.clear();
  state.exteriorFaces.clear();
  state.textureMetadata.clear();
  state.handleMetadata.clear();
  state.fixtureMetadata.clear();
  state.selectedObjects.clear();
  state.debugObjects.clear();
  state.debugHelperMetadata.clear();
  state.structureObjects.clear();
  state.objectMetadata.clear();
  state.highlightMetadata.clear();
  state.cutoutMetadata.clear();
  state.wallMetadata.clear();
  state.meshToObject.clear();
  state.objectMeshes.clear();
  state.objectRoots.clear();
  state.switchStates.clear();
  state.doorOpenState.clear();
  state.cabinetFrontOpenState.clear();
  state.proximityDoorsSuspended = false;
  state.doorCopiesByKey.clear();
  state.lightOriginalEmissive.clear();
  state.hasEditedModels = true;
  state.originalMaterials.clear();
  clearPendingMeshCallbacks();
  bumpMeshRegistryVersion();
};

// Debug helper - get counts of all tracked items
export const getStateCounts = () => ({
  highlightOverlays: state.highlightOverlays.size,
  highlightGroups: state.highlightGroups.size,
  proceduralGroups: state.proceduralGroups.size,
  openingGroups: state.openingGroups.size,
  interiorFaces: state.interiorFaces.size,
  exteriorFaces: state.exteriorFaces.size,
  textureMetadata: state.textureMetadata.size,
  handleMetadata: state.handleMetadata.size,
  fixtureMetadata: state.fixtureMetadata.size,
  selectedObjects: state.selectedObjects.size,
  debugObjects: state.debugObjects.size,
  debugHelperMetadata: state.debugHelperMetadata.size,
  structureObjects: state.structureObjects.size,
  objectMetadata: state.objectMetadata.size,
  cutoutMetadata: state.cutoutMetadata.size,
  wallMetadata: state.wallMetadata.size,
  meshToObject: state.meshToObject.size,
  objectMeshes: state.objectMeshes.size,
  objectRoots: state.objectRoots.size,
  pendingMeshCallbacks: state.pendingMeshCallbacks.size,
});
